import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, X, Trash2, Filter } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminComments() {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [deleteId, setDeleteId] = useState(null);
  const pendingOnly = searchParams.get('pending') === 'true';

  useEffect(() => {
    fetchComments();
  }, [page, pendingOnly]);

  const fetchComments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/comments`, {
        params: { page, limit: 20, pending_only: pendingOnly },
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments(response.data.items);
      setTotalPages(response.data.pages);
    } catch (error) {
      toast.error('Failed to fetch comments');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (commentId) => {
    try {
      await axios.patch(
        `${API_URL}/api/admin/comments/${commentId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Comment approved');
      fetchComments();
    } catch (error) {
      toast.error('Failed to approve comment');
    }
  };

  const handleReject = async (commentId) => {
    try {
      await axios.patch(
        `${API_URL}/api/admin/comments/${commentId}/reject`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Comment rejected');
      fetchComments();
    } catch (error) {
      toast.error('Failed to reject comment');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API_URL}/api/admin/comments/${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Comment deleted');
      setDeleteId(null);
      fetchComments();
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AdminLayout>
      <div data-testid="admin-comments">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#2D3E55]">
              Comments
            </h1>
            <p className="font-body text-slate-600 mt-1">
              Moderate blog comments
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant={!pendingOnly ? 'default' : 'outline'}
              onClick={() => setSearchParams({})}
              className={!pendingOnly ? 'bg-[#2D3E55]' : ''}
            >
              All
            </Button>
            <Button
              variant={pendingOnly ? 'default' : 'outline'}
              onClick={() => setSearchParams({ pending: 'true' })}
              className={pendingOnly ? 'bg-[#F15A24]' : ''}
              data-testid="filter-pending"
            >
              <Filter size={16} className="mr-2" />
              Pending
            </Button>
          </div>
        </div>

        {/* Comments List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="h-4 bg-slate-200 rounded w-1/3 mb-4"></div>
                  <div className="h-16 bg-slate-200 rounded mb-4"></div>
                  <div className="h-4 bg-slate-200 rounded w-1/4"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : comments.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="font-body text-slate-500">
                {pendingOnly ? 'No pending comments' : 'No comments yet'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {comments.map((comment) => (
              <Card key={comment.id} className="hover:shadow-md transition-shadow" data-testid={`comment-${comment.id}`}>
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="font-body font-semibold text-[#2D3E55]">
                          {comment.author_name}
                        </span>
                        <span className="text-slate-400">•</span>
                        <span className="font-body text-sm text-slate-500">
                          {comment.author_email}
                        </span>
                        <Badge className={comment.is_approved ? 'bg-green-500' : 'bg-yellow-500'}>
                          {comment.is_approved ? 'Approved' : 'Pending'}
                        </Badge>
                      </div>
                      <p className="font-body text-slate-700 mb-3">
                        {comment.content}
                      </p>
                      <p className="font-body text-xs text-slate-400">
                        Post ID: {comment.post_id} • {formatDate(comment.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {!comment.is_approved && (
                        <Button
                          size="sm"
                          onClick={() => handleApprove(comment.id)}
                          className="bg-green-500 hover:bg-green-600 text-white"
                          data-testid={`approve-${comment.id}`}
                        >
                          <Check size={16} className="mr-1" />
                          Approve
                        </Button>
                      )}
                      {comment.is_approved && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReject(comment.id)}
                          data-testid={`reject-${comment.id}`}
                        >
                          <X size={16} className="mr-1" />
                          Reject
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => setDeleteId(comment.id)}
                        data-testid={`delete-${comment.id}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            <Button
              variant="outline"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <span className="flex items-center px-4 font-body text-slate-600">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
            </Button>
          </div>
        )}

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Comment</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this comment? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}
